import { FC, Fragment, isValidElement, ReactNode } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';

import { Frame } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { AccessDeniedPage } from './access-denied-page';
import { environment, SiteMode } from './api';
import {
    ApiUsageReportPage,
    AppClientListPage,
    AppDetailsPage,
    AppListPage,
    RequestNewAppPage,
    WebhookManagementPage,
} from './apps';
import { AUTH_STORE_TOKEN } from './auth';
import { ClientsPage } from './clients';
import { PageSpinner } from './common';
import {
    ApiDetailsPage,
    ApiListPage,
    ReleaseDetailsPage,
    ReleaseListPage,
    TopicPage,
} from './docs';
import { HomePage, PublicHomePage } from './home';
import { CustomerIntegrationPathsPage } from './integration-paths';
import { Header } from './layout/components/header';
import { LoginPage as OktaLoginPage } from './okta';
import { isLocalPath, Paths } from './paths';
import { RequestAccessPage } from './request-access';
import { HtmlTermsPage, PartnerSiteTerms, PdfTermsPage, TermsStore } from './terms';
import { LoginPage as TokenServerLoginPage } from './token-server';

function unwrapFragments(children: ReactNode): ReactNode[] {
    /*
     * Note: Do not call React.Children.toArray(children) because it adds keys to each child,
     * preventing the "/apps/new" and "/apps/:id" routes from sharing the AppDetailsPageStore
     * and its appJustCreated banner state.
     */
    return (Array.isArray(children) ? (children as ReactNode[]) : [children]).flatMap(child =>
        isValidElement(child) && child.type === Fragment
            ? unwrapFragments(child.props.children as ReactNode)
            : child,
    );
}

const RedirectToHome: FC = () => <Redirect to="/" />;

const RewriteLegacyApiDetailsRoute: FC = () => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    let apiId = searchParams.get('api');
    let operationId = searchParams.get('operation');
    let path = '/docs/apis';
    if (apiId && operationId) {
        const webhooksSuffix = '-webhooks';
        if (apiId.endsWith(webhooksSuffix)) {
            apiId = apiId.slice(0, -webhooksSuffix.length);
            operationId = operationId.replace('-', '.').replace('-', '.');
            path = `/docs/apis/${apiId}/webhooks/${operationId}`;
        } else {
            path = `/docs/apis/${apiId}/endpoints/${operationId}`;
        }
    }
    return <Redirect to={path} />;
};

const DocumentationRoutes: FC = () => (
    <Switch>
        <Route path="/docs/apis" exact component={ApiListPage} />
        <Route
            path="/docs/apis/:apiId"
            render={({
                match: {
                    params: { apiId },
                },
            }) => <ApiDetailsPage key={apiId} apiId={apiId} />}
        />
        {environment.site.mode === SiteMode.TenantApis && (
            <Route path="/docs/release-notes">
                <Switch>
                    <Route path="/docs/release-notes" exact component={ReleaseListPage} />
                    <Route
                        path="/docs/release-notes/:version"
                        render={({
                            match: {
                                params: { version },
                            },
                        }) => <ReleaseDetailsPage version={version} />}
                    />
                </Switch>
            </Route>
        )}
        <Route
            path="/docs/:topicId/:subtopicId?"
            render={({
                match: {
                    params: { topicId, subtopicId },
                },
            }) => <TopicPage topicId={topicId} subtopicId={subtopicId} />}
        />
    </Switch>
);
DocumentationRoutes.displayName = 'DocumentationRoutes';

const UnauthenticatedRoutes: FC = () => (
    <Switch>
        <Route path="/api-details" component={RewriteLegacyApiDetailsRoute} />
        <Route path="/docs" component={DocumentationRoutes} />
        {environment.site.mode === SiteMode.TenantApis && (
            <Route path={Paths.customerIntegrationPaths} component={CustomerIntegrationPathsPage} />
        )}
        {isLocalPath(Paths.requestAccess) && (
            <Route path={Paths.requestAccess} component={RequestAccessPage} />
        )}
        {environment.site.mode === SiteMode.TenantApis && (
            <Route path="/signin" component={TokenServerLoginPage} />
        )}
        <Route path="/" exact component={PublicHomePage} />
        <Route path="/" component={RedirectToHome} />
    </Switch>
);
UnauthenticatedRoutes.displayName = 'UnauthenticatedRoutes';

const UnauthorizedRoutes: FC = () => (
    <Switch>
        <Route path="/api-details" component={RewriteLegacyApiDetailsRoute} />
        <Route path="/docs" component={DocumentationRoutes} />
        {environment.site.mode === SiteMode.TenantApis && (
            <Route path={Paths.customerIntegrationPaths} component={CustomerIntegrationPathsPage} />
        )}
        <Route path="/" exact component={AccessDeniedPage} />
        <Route path="/" component={RedirectToHome} />
    </Switch>
);
UnauthorizedRoutes.displayName = 'UnauthorizedRoutes';

const AuthorizedRoutes: FC = provide({ singletons: [TermsStore] })(
    observer(() => {
        const [termsStore] = useDependencies(TermsStore);

        const routes = (
            <Fragment>
                <Route path="/api-details" component={RewriteLegacyApiDetailsRoute} />
                <Route path="/docs" component={DocumentationRoutes} />
                {environment.site.mode === SiteMode.TenantApis && (
                    <Route
                        path={Paths.customerIntegrationPaths}
                        component={CustomerIntegrationPathsPage}
                    />
                )}
                {isLocalPath(Paths.requestAccess) && (
                    <Route path={Paths.requestAccess} component={RequestAccessPage} />
                )}
                {termsStore.accepted === null ? (
                    <Route path="/" component={PageSpinner} />
                ) : termsStore.accepted === false ? (
                    <Route path="/" exact>
                        {environment.site.appsTermsAndConditionsDocumentId === 1 ? (
                            <HtmlTermsPage>{PartnerSiteTerms}</HtmlTermsPage>
                        ) : (
                            <PdfTermsPage
                                url="/api-terms.pdf"
                                title="ServiceTitan, Inc. API Terms of Use"
                            />
                        )}
                    </Route>
                ) : (
                    <Fragment>
                        <Route path="/" exact component={HomePage} />
                        <Route path="/apps" exact component={AppListPage} />
                        <Route path="/apps/request-new" exact component={RequestNewAppPage} />
                        <Route path="/apps/new" component={AppDetailsPage} />
                        <Route
                            path="/apps/:id"
                            exact
                            render={({
                                match: {
                                    params: { id },
                                },
                            }) => <AppDetailsPage id={+id} />}
                        />
                        <Route
                            path="/apps/:id/clients"
                            exact
                            render={({
                                match: {
                                    params: { id },
                                },
                            }) => <AppClientListPage id={+id} />}
                        />
                        <Route
                            path="/apps/:id/api-usage-report"
                            exact
                            render={({
                                match: {
                                    params: { id },
                                },
                            }) => <ApiUsageReportPage id={+id} />}
                        />
                        <Route
                            path="/apps/:id/webhook-mgmt"
                            exact
                            render={({
                                match: {
                                    params: { id },
                                },
                            }) => <WebhookManagementPage id={+id} />}
                        />
                        <Route
                            path="/clients/:apiGroupName/:owner/:appId?"
                            render={({
                                match: {
                                    params: { apiGroupName, owner, appId },
                                },
                            }) => (
                                <ClientsPage
                                    apiGroupName={apiGroupName}
                                    owner={owner}
                                    appId={appId}
                                />
                            )}
                        />
                    </Fragment>
                )}
                <Route path="/" component={RedirectToHome} />
            </Fragment>
        );
        return <Switch>{unwrapFragments(routes)}</Switch>;
    }),
);
AuthorizedRoutes.displayName = 'AuthorizedRoutes';

export const Routes: FC = observer(() => {
    const [authStore] = useDependencies(AUTH_STORE_TOKEN);

    return (
        <Switch>
            {/* Note: "signin" is hardcoded by the Okta Sign-In Widget. */}
            {!authStore.isAuthenticated && environment.site.mode === SiteMode.PartnerApis && (
                <Route path="/signin" component={OktaLoginPage} />
            )}
            <Route path="/">
                <Frame header={<Header />}>
                    {authStore.isAuthenticated === null ? null : !authStore.isAuthenticated ? (
                        <UnauthenticatedRoutes />
                    ) : !authStore.isAuthorized ? (
                        <UnauthorizedRoutes />
                    ) : (
                        <AuthorizedRoutes />
                    )}
                </Frame>
            </Route>
        </Switch>
    );
});
Routes.displayName = 'Routes';
